import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import CardGrid from '../../components/cards/layout/CardGrid';
import CardContainer from '../../components/cards/layout/CardContainer';
import SkeletonCard from '../../components/cards/SkeletonCard';
import { useState } from 'react';
import { API_DELETE, DBRequest, useConfig } from '../../api/api';
import SpeedDialMenu from '../../components/menu/SpeedDialMenu';
import Feedback from '../../components/special/Feedback';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoneyLabel from '../../components/label/MoneyLabel';
import { numberToString } from '../../components/special/numberConverter';
import ExoAvatar from '../../components/exo/ExoAvatar';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import InventoryIcon from '@mui/icons-material/Inventory';
import useFetch from '../../api/useFetch';
import NewService from '../../components/new/NewService';
import ExoDialog from '../../components/exo/ExoDialog';
import RenderHTML from '../../components/preview/RenderHTML';

const ExtendService = ({ row, onUpdate, onClickEdit, onDelete, skr03 }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData, setApiData } = useFetch(`services/${row.id}`);

  const [open, setOpen] = useState(false);
  const [startData, setStartData] = useState(null);

  // menu
  const actions = [
    {
      icon: <ModeEditIcon />,
      name: t('Edit'),
      onClick: handleEdit,
    },
    {
      icon: <ContentCopyIcon />,
      name: t('Copy'),
      onClick: handleCopy,
    },
    { icon: <DeleteOutlineIcon />, name: t('Delete'), onClick: handleDelete },
  ];

  function handleEdit() {
    setStartData(apiData.data);
    setOpen(true);
  }

  function handleCopy() {
    const dataCopied = {};
    dataCopied.name = apiData.data.name;
    dataCopied.price = apiData.data.price;
    dataCopied.taxRate = apiData.data.taxRate;
    dataCopied.unit = apiData.data.unit;
    dataCopied.image = apiData.data.image;
    dataCopied.description = apiData.data.description;
    var categorieIds = [];
    apiData.data.serviceCategories.forEach((category) => {
      categorieIds.push(category.id);
    });
    dataCopied.serviceCategoryIds = categorieIds;
    setStartData(dataCopied);
    setOpen(true);
  }

  function handleUpdate(data) {
    const newData = { ...apiData, data };
    setOpen(false);
    setApiData(newData);
    onUpdate(newData.data);
  }

  // delete
  function handleDelete() {
    DBRequest({
      config,
      path: `services/${row.id}`,
      method: API_DELETE(),
      onFeedback: handleAlertOpen,
    });
    onDelete(row);
  }

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  const data = apiData?.data;
  const isPercent = data?.type === 'percent' || data?.type === 'percentEditable';

  return !apiData ? (
    <ServiceSkeleton />
  ) : (
    <CardGrid>
      {/* Header: Avatar + Name */}
      <CardContainer span={99}>
        <Box className="flex items-center gap-4 w-full px-2">
          <ExoAvatar
            picture={data.image && data.image.temporaryUrl}
            icon={<NoPhotographyIcon />}
            size="50px"
          />
          <Box className="flex-1">
            <Typography variant="h4" fontWeight={600}>
              {data.name}
            </Typography>
            {data.description && (
              <Box sx={{ color: colors.text, opacity: 0.5, mt: 0.5 }}>
                <RenderHTML HTML={data.description} />
              </Box>
            )}
          </Box>
        </Box>
      </CardContainer>

      {/* Type-specific pricing */}
      {isPercent ? (
        <>
          <StatCard
            label={t('Percent')}
            value={`${numberToString(data.amount ?? 0, 2)} %`}
          />
          {data.type === 'percentEditable' && (
            <StatCard
              label={`${t('Price')} ${t('Netto')}`}
              value={<MoneyLabel money={data.price} />}
            />
          )}
        </>
      ) : (
        <>
          <StatCard
            label={`${t('Price')} ${t('Netto')}`}
            value={<MoneyLabel money={data.price} />}
          />
          <StatCard
            label={`${t('Price')} ${t('Brutto')}`}
            value={
              <MoneyLabel money={data.price * (data.taxRate / 100 + 1)} />
            }
          />
          {Boolean(data.amount) && (
            <StatCard
              label={`${t('Amount')}${data.unit ? ` (${data.unit})` : ''}`}
              value={numberToString(data.amount, 2)}
            />
          )}
        </>
      )}

      {/* Revenue Stats */}
      <StatCard
        label={t('Sold Units')}
        value={data.totalSoldUnits ?? 0}
        icon={<InventoryIcon />}
      />
      <StatCard
        label={`${t('Revenue')} ${t('Netto')}`}
        value={<MoneyLabel money={data.totalRevenueNet ?? 0} />}
      />
      <StatCard
        label={`${t('Revenue')} ${t('Brutto')}`}
        value={<MoneyLabel money={data.totalRevenueGross ?? 0} />}
      />

      <SpeedDialMenu actions={actions} />
      <Feedback setState={setAlertState} state={alertState} />
      <ExoDialog open={open} limitWidth>
        <NewService
          fullWidth
          onClose={() => setOpen(false)}
          onCreate={handleUpdate}
          startData={startData}
        />
      </ExoDialog>
    </CardGrid>
  );
};
const StatCard = ({ label, value, icon }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <CardContainer>
      <Box className="flex flex-col items-center gap-1 py-2 px-3 w-full">
        {icon && (
          <Box className="mb-1" sx={{ color: colors.text }}>
            {icon}
          </Box>
        )}
        <Typography variant="h3" fontWeight={600} className="monospace" sx={{ color: colors.text }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text, opacity: 0.5 }}>
          {label}
        </Typography>
      </Box>
    </CardContainer>
  );
};

const ServiceSkeleton = () => {
  return (
    <CardGrid>
      <SkeletonCard height={70} span={99} />
      <SkeletonCard height={90} />
      <SkeletonCard height={90} />
      <SkeletonCard height={90} />
      <SkeletonCard height={90} />
      <SkeletonCard height={90} />
    </CardGrid>
  );
};

export default ExtendService;
